import { ButtonLink } from '@toggle/design-system';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { ToastPosition, useToasts } from '~/stores/use-toasts/useToasts';
import {
  getCustomSources,
  resetCustomSources,
  updateCustomSources,
} from '~/views/alfred/research/services/conversations-service';
import { createFaviconUrl } from '~/views/alfred/research/utils/createFaviconUrl';

import { InlineAlert } from './InlineAlert';
import { LinkInput } from './LinkInput';
import { SourcesUpdateAlert } from './SourcesUpdateAlert';
import * as S from './TrustedSourcesModal.styles';
import { WebsiteCard } from './WebsiteCard';

export const MAX_CUSTOM_SOURCES = 50;

export interface WebsiteInfo {
  id: string;
  title: string;
  url: string;
  favicon: string;
}

export const formatWebsiteInfo = (urls: string[]) =>
  urls.map(source => ({
    id: v4(),
    title: '',
    url: source,
    favicon: createFaviconUrl(source, 24),
  }));

interface TrustedSourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TrustedSourcesModal = ({
  isOpen,
  onClose,
}: TrustedSourcesModalProps) => {
  const { t } = useTranslation(['chat', 'common']);

  const [customSources, setCustomSources] = useState<WebsiteInfo[]>([]);
  const [resetting, setResetting] = useState(false);
  const [isOnlySource, setIsOnlySource] = useState(false);
  const sourcesContainerRef = useRef<HTMLDivElement>(null);

  const showToast = useToasts(state => state.showToast);

  const existedSources = customSources.map(source => source.url);

  const handleAddWebsites = (websites: WebsiteInfo[]) => {
    const uniqueWebsites = websites.filter(
      website => !existedSources.includes(website.url)
    );
    const newSources = [...customSources, ...uniqueWebsites];
    setCustomSources(newSources);

    if (newSources.length > 1) {
      setIsOnlySource(false);
    }
  };

  const handleResetSources = async () => {
    const response = await resetCustomSources();

    if (response) {
      setResetting(false);
      onClose();
    }

    if (response.data) {
      showToast({
        id: 'reset-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert updateType="reset" />,
      });
    }
  };

  const handleUpdateCustomSources = async () => {
    const response = await updateCustomSources({
      sources: customSources.map(source => source.url),
    });

    if (response) {
      onClose();
    }

    if (response.data) {
      showToast({
        id: 'update-sources-success',
        position: ToastPosition.TopRight,
        content: <SourcesUpdateAlert updateType="update" />,
      });
    }
  };

  const handleRemoveSource = (id: string) => {
    if (customSources.length === 1) {
      setIsOnlySource(true);
    } else {
      setCustomSources(customSources.filter(source => source.id !== id));
    }
  };

  useEffect(() => {
    const fetchCustomSources = async () => {
      const result = await getCustomSources();

      if (result.data?.sources) {
        const uniqueSources = result.data.sources.filter(
          source => !existedSources.includes(source)
        );
        setCustomSources(formatWebsiteInfo(uniqueSources));
      }
    };

    fetchCustomSources();
  }, []);

  useEffect(() => {
    if (sourcesContainerRef.current && customSources.length) {
      sourcesContainerRef.current.scrollTop =
        sourcesContainerRef.current.scrollHeight;
    }
  }, [customSources.length]);

  const getModalProps = () => {
    if (resetting) {
      return {
        title: t('chat:trustedSources.modal.resetTitle'),
        primaryBtnProps: {
          label: t('chat:trustedSources.modal.resetConfirmation'),
          onClick: handleResetSources,
        },
        cancelBtnProps: {
          label: t('common:cancel'),
          onClick: () => setResetting(false),
        },
      };
    }

    return {
      title: t('chat:trustedSources.modal.title'),
      description: t('chat:trustedSources.modal.description'),
      primaryBtnProps: {
        label: t('common:update'),
        onClick: handleUpdateCustomSources,
      },
      cancelBtnProps: {
        label: t('common:cancel'),
        onClick: onClose,
      },
    };
  };

  return (
    <S.StyledModal
      isOpen={isOpen}
      onClose={() => {
        setCustomSources([]);
        onClose();
      }}
      {...getModalProps()}
      disableEnterClose
      outsideClickClose={false}
      data-testid="trusted-sources-modal"
      footerLeftAction={
        resetting ? null : (
          <ButtonLink
            label={t('chat:trustedSources.modal.reset')}
            onClick={() => setResetting(true)}
          />
        )
      }
    >
      <S.Content>
        {resetting ? (
          <S.ResetDescription>
            {t('chat:trustedSources.modal.resetDescription')}
          </S.ResetDescription>
        ) : (
          <>
            <S.Sources ref={sourcesContainerRef}>
              {customSources.map(website => (
                <WebsiteCard
                  key={website.id}
                  {...website}
                  handleRemove={handleRemoveSource}
                />
              ))}
              {isOnlySource && <InlineAlert alertType="onlyOneSource" />}
            </S.Sources>
            {customSources.length >= MAX_CUSTOM_SOURCES ? (
              <InlineAlert alertType="exceededLimit" />
            ) : (
              <LinkInput
                sourceCount={customSources.length}
                handleAddWebsites={handleAddWebsites}
              />
            )}
          </>
        )}
      </S.Content>
    </S.StyledModal>
  );
};
